import React, { useState, useEffect, useCallback } from 'react';
import ConfirmationForm from '../components/ConfirmationForm';
import QuestionNavigatorSidebar from '../components/QuestionNavigatorSidebar';
import TimeExtensionPopup from '../components/TimeExtensionPopup';
import AnswerOptions from '../components/AnswerOptions';
import QuestionNavigationButtons from '../components/QuestionNavigationButtons';
import FlagQuestionDialog from '../components/FlagQuestionDialog';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  getQuiz, 
  getQuestionsForQuiz, 
  addUserAnswer,
  skipQuestion,
  submitQuiz,
  updateQuizStart,
  getUserSession,
  flagQuestion,
  isSuccessResponse,
  getResponseData,
  getErrorMessage 
} from '../components/apiClient.js';
import { getQuizQuestionTime } from '../utils/quizTiming.js';
import { 
  loadQuizState, 
  clearQuizState, 
  hasQuizState,
  saveQuizStateThrottled,
  QuizSessionState 
} from '../utils/quizStateManager';

// Interface for individual question from API
interface Question {
  id: number;
  question_id: number;
  question_img_url: string;
  has_multiple_answer: number;
  duration: number;
  option_count: number;
  exam_id: number;
  subject_id: number;
  chapter_id: number;
  topic_id: number;
  level: string;
  correct_option: string;
  solution: string;
  option_1?: string;
  option_2?: string;
  option_3?: string;
  option_4?: string;
  option_5?: string;
  question_order?: number;
}

// Interface for quiz details from API
interface QuizDetails {
  id: number;
  name: string;
  description: string;
  class_id: number;
  subject_id: number;
  exam_id: number;
  start_date: string;
  quiz_detail_image: string;
  youtube_video_id: string;
  is_live: number;
  marking: string;
  quiz_type: string;
  user_id: number;
  level: string; // Quiz difficulty level for timing
}

// Interface for question state tracking
interface QuestionState {
  selectedOption: number | null;
  timeRemaining: number; // Per-question time remaining in seconds
  visited: boolean;
  skipped: boolean; // Track if question was skipped
  timeSpent: number; // Time actually spent on this question
  startTime: number; // When this question was first visited
}

const QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { quizReference } = useParams<{ quizReference: string }>();
  
  // State for quiz data from API
  const [quizDetails, setQuizDetails] = useState<QuizDetails | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Quiz state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionStates, setQuestionStates] = useState<QuestionState[]>([]);
  const [isQuizActive, setIsQuizActive] = useState(false);
  const [totalTimeRemaining, setTotalTimeRemaining] = useState(0);
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const [userSession, setUserSession] = useState<any>(null);
  
  // Time extension state
  const [extensionsUsed, setExtensionsUsed] = useState(0);
  const [showExtensionDialog, setShowExtensionDialog] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showStateRestoredNotification, setShowStateRestoredNotification] = useState(false);
  const [extensionDialogShown, setExtensionDialogShown] = useState(false); // Track if dialog was already shown

  // Question filter state
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set(['all']));
  
  // Answer saving state
  const [isSavingAnswer, setIsSavingAnswer] = useState(false);
  
  // Quiz submission confirmation state
  const [showSubmissionDialog, setShowSubmissionDialog] = useState(false);
  
  // Flag question state
  const [showFlagQuestionDialog, setShowFlagQuestionDialog] = useState(false);
  const [isFlaggingQuestion, setIsFlaggingQuestion] = useState(false);
  const [flaggedQuestions, setFlaggedQuestions] = useState<Set<number>>(new Set());
  const [showFlagSuccessNotification, setShowFlagSuccessNotification] = useState(false);
  
  // Mobile UI state
  const [showNavigatorDrawer, setShowNavigatorDrawer] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageScale, setImageScale] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // State restoration flag
  const [stateRestored, setStateRestored] = useState(false);

  // Function to create current state snapshot for saving
  const getCurrentQuizState = useCallback((): QuizSessionState | null => {
    if (!quizReference || !quizDetails || questions.length === 0) {
      return null;
    }

    return {
      quizReference,
      quizDetails,
      questions,
      currentQuestionIndex,
      questionStates,
      isQuizActive,
      totalTimeRemaining,
      lastUpdateTime,
      userSession,
      extensionsUsed,
      extensionDialogShown,
      activeFilters: Array.from(activeFilters),
      savedAt: Date.now()
    };
  }, [
    quizReference, quizDetails, questions, currentQuestionIndex, questionStates,
    isQuizActive, totalTimeRemaining, lastUpdateTime, userSession,
    extensionsUsed, extensionDialogShown, activeFilters
  ]);

  // Manual save function for immediate state saving
  const saveStateNow = useCallback(() => {
    if (stateRestored && isQuizActive && quizReference) {
      const currentState = getCurrentQuizState();
      if (currentState) {
        try {
          sessionStorage.setItem('wizi_quiz_state', JSON.stringify(currentState));
          console.log('Quiz state manually saved - Question:', currentState.currentQuestionIndex + 1, 'Time:', currentState.totalTimeRemaining);
        } catch (error) {
          console.error('Failed to manually save state:', error);
        }
      }
    }
  }, [stateRestored, isQuizActive, quizReference, getCurrentQuizState]);

  // Function to restore state from sessionStorage
  const restoreQuizState = useCallback((savedState: QuizSessionState) => {
    console.log('Restoring quiz state from sessionStorage');
    
    try {
      setQuizDetails(savedState.quizDetails);
      setQuestions(savedState.questions);
      setCurrentQuestionIndex(savedState.currentQuestionIndex);
      setQuestionStates(savedState.questionStates);
      setIsQuizActive(savedState.isQuizActive);
      setTotalTimeRemaining(savedState.totalTimeRemaining);
      setLastUpdateTime(savedState.lastUpdateTime);
      setUserSession(savedState.userSession);
      setExtensionsUsed(savedState.extensionsUsed);
      setExtensionDialogShown(savedState.extensionDialogShown);
      setActiveFilters(new Set(savedState.activeFilters));
      setStateRestored(true);
      
      // Show restoration notification
      setShowStateRestoredNotification(true);
      setTimeout(() => setShowStateRestoredNotification(false), 4000);
      
      console.log('Quiz state restored successfully');
    } catch (error) {
      console.error('Error restoring quiz state:', error);
      // Clear invalid state and continue with fresh load
      clearQuizState();
    }
  }, []);

  // Save quiz state when significant changes occur
  useEffect(() => {
    if (stateRestored && isQuizActive && quizReference) {
      const currentState = getCurrentQuizState();
      if (currentState) {
        saveQuizStateThrottled(currentState);
      }
    }
  }, [
    currentQuestionIndex, questionStates, totalTimeRemaining, extensionsUsed,
    extensionDialogShown, isQuizActive, stateRestored, quizReference, getCurrentQuizState
  ]);

  // Handle page refresh/navigation - save state before unload
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isQuizActive && stateRestored && quizReference) {
        const currentState = getCurrentQuizState();
        if (currentState) {
          // Use synchronous save for beforeunload
          try {
            sessionStorage.setItem('wizi_quiz_state', JSON.stringify(currentState));
          } catch (error) {
            console.error('Failed to save state before unload:', error);
          }
        }
        
        // Show confirmation dialog to user
        const message = 'Your quiz progress will be saved. Are you sure you want to leave?';
        event.returnValue = message;
        return message;
      }
    };

    const handlePopState = (event: PopStateEvent) => {
      if (isQuizActive && stateRestored && quizReference) {
        // Save state when back button is pressed
        const currentState = getCurrentQuizState();
        if (currentState) {
          try {
            sessionStorage.setItem('wizi_quiz_state', JSON.stringify(currentState));
            console.log('Quiz state saved due to back button navigation');
          } catch (error) {
            console.error('Failed to save state on back navigation:', error);
          }
        }

        // Optionally, you can prevent navigation and show a confirmation
        // Note: This requires pushing a new state to the history stack
        const shouldLeave = window.confirm(
          'Your quiz progress will be saved. Are you sure you want to leave the quiz?'
        );
        
        if (!shouldLeave) {
          // Push the current state back to prevent navigation
          window.history.pushState(null, '', window.location.href);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isQuizActive, stateRestored, quizReference, getCurrentQuizState]);

  // Push initial state to help with back button detection
  useEffect(() => {
    if (isQuizActive && stateRestored) {
      // Push a state to the history stack to detect back button presses
      window.history.pushState({ quizActive: true }, '', window.location.href);
    }
  }, [isQuizActive, stateRestored]);

  // Clear quiz state when component unmounts (if quiz is completed)
  useEffect(() => {
    return () => {
      if (isQuizActive && stateRestored && quizReference) {
        // Save state when component unmounts during navigation
        const currentState = getCurrentQuizState();
        if (currentState) {
          try {
            sessionStorage.setItem('wizi_quiz_state', JSON.stringify(currentState));
            console.log('Quiz state saved during component unmount');
          } catch (error) {
            console.error('Failed to save state during unmount:', error);
          }
        }
      } else if (!isQuizActive && stateRestored) {
        // Only clear state if quiz is not active (i.e., completed or submitted)
        clearQuizState();
      }
    };
  }, [isQuizActive, stateRestored, quizReference, getCurrentQuizState]);

  // Toggle filter function
  const toggleFilter = (filterType: string) => {
    setActiveFilters(prev => {
      const newFilters = new Set(prev);
      
      if (filterType === 'all') {
        // If 'all' is clicked, clear other filters and set only 'all'
        return new Set(['all']);
      } else {
        // Remove 'all' if it exists when clicking specific filters
        newFilters.delete('all');
        
        if (newFilters.has(filterType)) {
          newFilters.delete(filterType);
        } else {
          newFilters.add(filterType);
        }
        
        // If no specific filters are selected, default back to 'all'
        if (newFilters.size === 0) {
          newFilters.add('all');
        }
      }
      
      return newFilters;
    });
  };

  // Filter questions based on active filters
  const getFilteredQuestions = () => {
    if (activeFilters.has('all')) {
      return questions.map((_, index) => index);
    }
    
    return questions.map((_, index) => index).filter(index => {
      const state = questionStates[index];
      const isAnswered = state?.selectedOption !== null;
      const isSkipped = state?.skipped;
      const isViewed = state?.visited && !isAnswered && !isSkipped;
      const isUnvisited = !state?.visited;
      
      return (
        (activeFilters.has('completed') && isAnswered) ||
        (activeFilters.has('skipped') && isSkipped) ||
        (activeFilters.has('viewed') && isViewed) ||
        (activeFilters.has('unvisited') && isUnvisited)
      );
    });
  };

  // Calculate quiz submission stats
  const getQuizStats = () => {
    const totalQuestions = questions.length;
    
    // Safety check: ensure all answered questions are marked as visited
    // This handles edge cases where a question was answered but not properly marked as visited
    let needsStateUpdate = false;
    const correctedStates = questionStates.map(state => {
      if (state && state.selectedOption !== null && !state.visited) {
        needsStateUpdate = true;
        return { ...state, visited: true };
      }
      return state;
    });
    
    // Update the actual state if we found inconsistencies
    if (needsStateUpdate) {
      console.log('Fixing question states: marking answered questions as visited');
      setQuestionStates(correctedStates);
    }
    
    const answeredQuestions = correctedStates.filter(state => state?.selectedOption !== null).length;
    const skippedQuestions = correctedStates.filter(state => state?.skipped).length;
    // Fix: Explicitly check for null instead of using falsy check (0 is a valid option!)
    const viewedQuestions = correctedStates.filter(state => state?.visited && state?.selectedOption === null && !state?.skipped).length;
    // Fix: Directly count unvisited questions instead of subtracting
    const unvisitedQuestions = correctedStates.filter(state => !state?.visited).length;
    
    // Debug logging to understand the issue
    console.log('Quiz Stats Debug:', {
      totalQuestions,
      answeredQuestions,
      skippedQuestions,
      viewedQuestions,
      unvisitedQuestions,
      needsStateUpdate,
      correctedStates: correctedStates.map((state, index) => ({
        index,
        visited: state?.visited,
        selectedOption: state?.selectedOption,
        skipped: state?.skipped
      }))
    });
    
    return {
      totalQuestions,
      answeredQuestions,
      skippedQuestions,
      viewedQuestions,
      unvisitedQuestions,
      completionPercentage: Math.round((answeredQuestions / totalQuestions) * 100)
    };
  };

  // Handle submission confirmation
  const handleSubmissionConfirm = () => {
    setShowSubmissionDialog(false);
    handleSubmitQuiz();
  };

  // Show submission confirmation dialog
  const showSubmissionConfirmation = () => {
    setShowSubmissionDialog(true);
  };

  // Load quiz details and questions
  useEffect(() => {
    const loadQuizData = async () => {
      if (!quizReference) {
        setError('Quiz reference not provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Check for saved state first
        const savedState = loadQuizState(quizReference);
        if (savedState) {
          console.log('Found saved quiz state, restoring...');
          restoreQuizState(savedState);
          setLoading(false);
          return;
        }

        console.log('No saved state found, loading fresh quiz data...');
        
        // Get user session
        const session = getUserSession();
        setUserSession(session);
        
        // Load quiz details
        const quizResponse = await getQuiz(quizReference);
        if (!isSuccessResponse(quizResponse)) {
          throw new Error(getErrorMessage(quizResponse) || 'Failed to load quiz details');
        }
        
        const quiz = getResponseData(quizResponse);
        setQuizDetails(quiz);
        
        // Load questions for the quiz
        const questionsResponse = await getQuestionsForQuiz(quizReference);
        if (!isSuccessResponse(questionsResponse)) {
          throw new Error(getErrorMessage(questionsResponse) || 'Failed to load quiz questions');
        }
        
        const questionsData = getResponseData(questionsResponse);
        setQuestions(questionsData);
        
        // Initialize question states
        // Determine question timing based on quiz level
        const quizLevelTime = getQuizQuestionTime(quiz);
        console.log(`Quiz level: ${quiz.level}, Question time: ${quizLevelTime} seconds`);

        const initialStates = questionsData.map((question: Question, index: number) => {
          // Use quiz level for timing instead of individual question duration
          // Individual question duration is now ignored in favor of consistent quiz-level timing
          const questionTime = quizLevelTime;
          
          // Debug logging
          console.log(`Question ${index + 1} (Level: ${question.level}) - Using quiz-level time: ${questionTime} seconds`);
          
          return {
            selectedOption: null,
            timeRemaining: questionTime,
            visited: false,
            skipped: false,
            timeSpent: 0,
            startTime: 0
          };
        });
        
        setQuestionStates(initialStates);
        
        // Calculate total time
        const totalTime = initialStates.reduce((total: number, state: QuestionState) => total + state.timeRemaining, 0);
        setTotalTimeRemaining(totalTime);
        
        setIsQuizActive(true);
        setLastUpdateTime(Date.now());
        setStateRestored(true); // Mark as ready for state saving
        
        // Update quiz start time in database
        if (session?.id) {
          try {
            await updateQuizStart(quiz.id, session.id);
          } catch (startErr) {
            console.error('Error updating quiz start time:', startErr);
            // Don't fail the quiz loading if start time update fails
          }
        }
        
      } catch (err) {
        console.error('Error loading quiz data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load quiz');
      } finally {
        setLoading(false);
      }
    };

    loadQuizData();
  }, [quizReference, restoreQuizState]);

  // Save user answer to API
  const saveUserAnswer = async (questionId: number, selectedOption: number, duration: number) => {
    if (!userSession?.id || !quizDetails?.id) return;

    setIsSavingAnswer(true);
    try {
      const answerData = {
        user_id: userSession.id,
        quiz_id: quizDetails.id,
        question_id: questionId,
        duration: duration,
        option_answer: selectedOption
      };

      const response = await addUserAnswer(answerData);
      if (!isSuccessResponse(response)) {
        console.error('Failed to save answer:', getErrorMessage(response));
      }
    } catch (err) {
      console.error('Error saving answer:', err);
    } finally {
      setIsSavingAnswer(false);
    }
  };

  // Format time as MM:SS
  const formatTime = useCallback((seconds: number): string => {
    // Validate and sanitize the input
    const sanitizedSeconds = Math.floor(Math.abs(Number(seconds) || 0));
    const minutes = Math.floor(sanitizedSeconds / 60);
    const remainingSeconds = sanitizedSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  // Handle option selection with API call
  const handleOptionSelect = async (optionIndex: number) => {
    if (!isQuizActive) return;
    
    const currentTime = Date.now();
    const timeSpent = currentTime - (questionStates[currentQuestionIndex]?.startTime || currentTime);
    
    // Save answer to API
    await saveUserAnswer(questions[currentQuestionIndex].id, optionIndex + 1, Math.floor(timeSpent / 1000));
    
    setQuestionStates(prev => {
      const updated = [...prev];
      updated[currentQuestionIndex] = {
        ...updated[currentQuestionIndex],
        selectedOption: optionIndex,
        visited: true // Ensure question is marked as visited when answered
      };
      return updated;
    });

    // Immediately save state after answer selection
    setTimeout(() => saveStateNow(), 100);
  };

  // Quiz submission handler
  const handleSubmitQuiz = useCallback(async () => {
    if (!userSession?.id || !quizDetails?.id) return;
    
    setIsQuizActive(false);
    
    try {
      // Call submit quiz API to ensure complete data
      const submitData = {
        user_id: userSession.id,
        quiz_id: quizDetails.id
      };

      const response = await submitQuiz(submitData);
      if (!isSuccessResponse(response)) {
        console.error('Failed to submit quiz:', getErrorMessage(response));
      }
    } catch (err) {
      console.error('Error submitting quiz:', err);
    }
    
    // Clear saved quiz state since quiz is now completed
    clearQuizState();
    console.log('Quiz completed, cleared saved state');
    
    // Navigate to results page
    navigate(`/quiz-results-detailed/${quizReference}`, {
      state: {
        quizReference,
        quizDetails,
        questions,
        questionStates,
        totalQuestions: questions.length
      }
    });
  }, [navigate, quizReference, quizDetails, questions, questionStates, userSession]);

  // Mark current question as visited when component mounts or question changes
  useEffect(() => {
    if (questions.length > 0) {
      setQuestionStates(prev => {
        const updated = [...prev];
        if (updated[currentQuestionIndex]) {
          updated[currentQuestionIndex] = {
            ...updated[currentQuestionIndex],
            visited: true,
            startTime: updated[currentQuestionIndex].startTime || Date.now()
          };
        }
        return updated;
      });
    }
  }, [currentQuestionIndex, questions.length]);

  // Time extension handlers
  const handleTimeExtension = (extend: boolean) => {
    setShowExtensionDialog(false);
    setTimerPaused(false);
    // Reset last update time to current time to prevent timer from trying to "catch up"
    setLastUpdateTime(Date.now());
    
    if (extend && extensionsUsed < 2) {
      // Add 2 minutes (120 seconds) to total time
      setTotalTimeRemaining(prev => prev + 120);
      setExtensionsUsed(prev => prev + 1);
      
      // Reset the dialog shown flag so it can appear again if time runs low
      setExtensionDialogShown(false);
      
      // Show success notification
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 3000);
    }
    // If extend is false, the dialog shown flag remains true to prevent reappearing
    // The dialog will close and timer will resume normally with remaining time
  };

  // Check if time extension dialog should be shown
  const checkTimeExtension = useCallback(() => {
    // Show dialog when 1 minute (60 seconds) remaining and extensions available
    // Only show if not already shown for this time period
    if (totalTimeRemaining <= 60 && totalTimeRemaining > 0 && 
        extensionsUsed < 2 && !showExtensionDialog && !timerPaused && !extensionDialogShown) {
      setShowExtensionDialog(true);
      setTimerPaused(true);
      setExtensionDialogShown(true); // Mark as shown to prevent reappearing
      // Update last time to prevent timer issues when resuming
      setLastUpdateTime(Date.now());
    }
  }, [totalTimeRemaining, extensionsUsed, showExtensionDialog, timerPaused, extensionDialogShown]);

  // Timer logic - updates every second
  useEffect(() => {
    if (!isQuizActive || questions.length === 0 || timerPaused) return;

    const timer = setInterval(() => {
      const now = Date.now();
      const deltaSeconds = Math.floor((now - lastUpdateTime) / 1000);
      
      // Validate deltaSeconds to prevent time jumps or negative values
      if (deltaSeconds >= 1 && deltaSeconds <= 10) { // Max 10 seconds to handle tab switching
        setLastUpdateTime(now);
        
        setQuestionStates(prev => {
          const updated = [...prev];
          const currentState = updated[currentQuestionIndex];
          
          if (currentState && currentState.timeRemaining > 0) {
            updated[currentQuestionIndex] = {
              ...currentState,
              timeRemaining: Math.max(0, currentState.timeRemaining - deltaSeconds),
              timeSpent: currentState.timeSpent + deltaSeconds
            };
          }
          
          return updated;
        });
        
        // Update total quiz time
        setTotalTimeRemaining(prev => {
          const currentTime = Math.max(0, prev);
          const newTime = Math.max(0, currentTime - deltaSeconds);
          
          // Check for time extension before submitting
          if (newTime <= 60 && newTime > 0 && extensionsUsed < 2 && !showExtensionDialog) {
            setTimeout(() => checkTimeExtension(), 0);
          }
          
          if (newTime === 0) {
            setIsQuizActive(false);
            handleSubmitQuiz();
          }
          return newTime;
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isQuizActive, lastUpdateTime, currentQuestionIndex, handleSubmitQuiz, questions.length, timerPaused, totalTimeRemaining, extensionsUsed, showExtensionDialog, checkTimeExtension]);

  // Navigation handlers
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      // Save state after navigation
      setTimeout(() => saveStateNow(), 100);
    }
  };

  const handleNext = async () => {
    if (currentQuestionIndex < questions.length - 1) {
      // Check if current question is unanswered and not already skipped
      const currentState = questionStates[currentQuestionIndex];
      const isUnanswered = currentState?.selectedOption === null;
      const isNotSkipped = !currentState?.skipped;
      
      // If question is unanswered and not skipped, automatically skip it
      if (isUnanswered && isNotSkipped && userSession?.id && quizDetails?.id) {
        const currentTime = Date.now();
        const timeSpent = currentTime - (currentState?.startTime || currentTime);
        
        try {
          // Call skip API
          const skipData = {
            user_id: userSession.id,
            quiz_id: quizDetails.id,
            question_id: questions[currentQuestionIndex].question_id,
            quiz_question_id: questions[currentQuestionIndex].id,
            duration: Math.floor(timeSpent / 1000)
          };

          const response = await skipQuestion(skipData);
          if (!isSuccessResponse(response)) {
            console.error('Failed to skip question:', getErrorMessage(response));
          }
        } catch (err) {
          console.error('Error skipping question:', err);
        }

        // Update local state to mark as skipped
        setQuestionStates(prev => {
          const newStates = [...prev];
          newStates[currentQuestionIndex] = {
            ...newStates[currentQuestionIndex],
            skipped: true,
            timeSpent: Math.floor(timeSpent / 1000)
          };
          return newStates;
        });
      }
      
      // Navigate to next question
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      // Save state after navigation
      setTimeout(() => saveStateNow(), 100);
    }
  };

  const handleSkip = async () => {
    if (!isQuizActive || !userSession?.id || !quizDetails?.id) return;
    
    const currentTime = Date.now();
    const timeSpent = currentTime - (questionStates[currentQuestionIndex]?.startTime || currentTime);
    
    try {
      // Call skip API
      const skipData = {
        user_id: userSession.id,
        quiz_id: quizDetails.id,
        question_id: questions[currentQuestionIndex].question_id,
        quiz_question_id: questions[currentQuestionIndex].id,
        duration: Math.floor(timeSpent / 1000)
      };

      const response = await skipQuestion(skipData);
      if (!isSuccessResponse(response)) {
        console.error('Failed to skip question:', getErrorMessage(response));
      }
    } catch (err) {
      console.error('Error skipping question:', err);
    }

    // Update local state to mark as skipped
    setQuestionStates(prev => {
      const newStates = [...prev];
      newStates[currentQuestionIndex] = {
        ...newStates[currentQuestionIndex],
        skipped: true,
        timeSpent: Math.floor(timeSpent / 1000)
      };
      return newStates;
    });

    // Navigate to next question
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }

    // Save state after skipping
    setTimeout(() => saveStateNow(), 100);
  };

  const handleFlagQuestion = async () => {
    if (!isQuizActive || !currentQuestion?.id || isFlaggingQuestion) return;
    
    setIsFlaggingQuestion(true);
    
    try {
      const response = await flagQuestion(currentQuestion.id, 'invalid_question');
      
      if (isSuccessResponse(response)) {
        // Mark question as flagged locally
        setFlaggedQuestions(prev => {
          const newSet = new Set(prev);
          newSet.add(currentQuestion.id);
          return newSet;
        });
        
        // Show success notification
        setShowFlagSuccessNotification(true);
        setTimeout(() => setShowFlagSuccessNotification(false), 5000);
        
        // Close dialog
        setShowFlagQuestionDialog(false);
        
        console.log('Question flagged successfully');
      } else {
        console.error('Failed to flag question:', getErrorMessage(response));
        alert('Failed to report question. Please try again.');
      }
    } catch (err) {
      console.error('Error flagging question:', err);
      alert('An error occurred while reporting the question. Please try again.');
    } finally {
      setIsFlaggingQuestion(false);
    }
  };

  // Loading state
  if (loading) {
    const isRestoring = hasQuizState(quizReference || '');
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9ff' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <h4 className="text-muted">
            {isRestoring ? 'Restoring Quiz...' : 'Loading Quiz...'}
          </h4>
          <p className="text-muted">
            {isRestoring 
              ? 'Restoring your previous progress' 
              : 'Please wait while we prepare your quiz'
            }
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !quizDetails || questions.length === 0) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9ff' }}>
        <div className="text-center">
          <div className="text-danger mb-3">
            <i className="fas fa-exclamation-triangle fa-3x"></i>
          </div>
          <h4 className="text-danger">Error Loading Quiz</h4>
          <p className="text-muted">{error || 'Quiz not found or no questions available'}</p>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/custom-quiz-generator')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Quiz Generator
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentState = questionStates[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <div className="vh-92 d-flex flex-column" style={{ backgroundColor: '#f8f9ff' }}>
      {/* Quiz Header with Global Timer - Fixed at top */}
      <div className="card border-0 shadow-sm position-sticky top-0" style={{ borderRadius: '0 0 20px 20px', zIndex: 1030 }}>
        <div className="card-body p-2 p-md-3" style={{
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          borderRadius: '0 0 20px 20px'
        }}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                {/* Hide quiz name on mobile, show on desktop */}
                {/* <h4 className="text-white fw-bold mb-1 d-none d-md-block">
                  {quizDetails.name}
                </h4> */}
                <p className="text-white opacity-90 mb-1 small">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </p>
                <div className="progress" style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.3)' }}>
                  <div 
                    className="progress-bar bg-light" 
                    style={{ width: `${progress}%`, transition: 'width 0.3s ease' }}
                  ></div>
                </div>
              </div>
              <div className="col-md-6 text-md-end mt-1 mt-md-0">
                <div className="d-flex justify-content-md-end justify-content-center gap-2 gap-md-4">
                  {/* Total Quiz Timer */}
                  <div className="text-center">
                    <div className="text-white opacity-90 small" style={{ fontSize: '0.7rem' }}>Total Time</div>
                    <div className={`fw-bold ${extensionsUsed > 0 ? 'text-warning' : 'text-white'}`} style={{ fontSize: '0.9rem' }}>
                      <i className="fas fa-clock me-1"></i>
                      {formatTime(totalTimeRemaining)}
                      {extensionsUsed > 0 && (
                        <span className="small ms-1">
                          <i className="fas fa-plus-circle" title={`Extended ${extensionsUsed} time(s)`}></i>
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Current Question Timer */}
                  <div className="text-center">
                    <div className="text-white opacity-90 small" style={{ fontSize: '0.7rem' }}>Question Time</div>
                    <div className={`fw-bold ${currentState?.timeRemaining <= 10 ? 'text-warning' : 'text-white'}`} style={{ fontSize: '0.9rem' }}>
                      <i className="fas fa-hourglass-half me-1"></i>
                      {formatTime(currentState?.timeRemaining || 0)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Quiz Content */}
      <div className="flex-grow-1 d-flex" style={{ paddingBottom: '80px' }}>
        <div className="container-fluid h-90">
          <div className="row h-100 g-0">
            {/* Question Panel */}
            <div className="col-12 col-lg-8 d-flex flex-column">
              <div className="flex-grow-1">
                <div className="card border-0 shadow-sm h-100" style={{padding: '0px', borderRadius: '20px' }}>
                  <div className="card-body d-flex flex-column">
                    {/* Question Text or Image */}
                    <div className="">
                      {currentQuestion.question_img_url ? (
                        <div className="text-center">
                          <img 
                            src={currentQuestion.question_img_url} 
                            alt="Question" 
                            className="img-fluid cursor-pointer" 
                            style={{ 
                              maxHeight: window.innerWidth < 768 ? '180px' : '300px', 
                              borderRadius: '12px',
                              cursor: 'pointer'
                            }}
                            onClick={() => setShowImageModal(true)}
                          />
                          <div className="small text-muted mt-1 d-md-none">
                            <i className="fas fa-expand-arrows-alt me-1"></i>
                            Tap to view full size
                          </div>
                        </div>
                      ) : (
                        <h3 className="fw-bold mb-2" style={{ lineHeight: '1.4', fontSize: window.innerWidth < 768 ? '1.2rem' : '1.5rem' }}>
                          Question {currentQuestionIndex + 1}
                        </h3>
                      )}
                      
                      {/* Time Warning */}
                      {currentState?.timeRemaining <= 10 && currentState?.timeRemaining > 0 && (
                        <div className="alert alert-warning border-0" style={{ borderRadius: '12px' }}>
                          <i className="fas fa-exclamation-triangle me-2"></i>
                          <strong>Time Warning:</strong> Only {currentState.timeRemaining} seconds left!
                        </div>
                      )}

                      {/* Flag Question Button */}
                      <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
                        <div className="flex-grow-1"></div>
                        <button
                          type="button"
                          className={`btn btn-sm ${flaggedQuestions.has(currentQuestion.id) ? 'btn-warning' : 'btn-outline-warning'}`}
                          onClick={() => setShowFlagQuestionDialog(true)}
                          disabled={!isQuizActive || isFlaggingQuestion}
                          title={flaggedQuestions.has(currentQuestion.id) ? 'Question already flagged' : 'Report this question as invalid'}
                        >
                          <i className="fas fa-flag me-1"></i>
                          {flaggedQuestions.has(currentQuestion.id) ? 'Flagged' : 'Report Error'}
                        </button>
                      </div>
                    </div>

                    {/* Answer Options - Clickable Buttons */}
                    <AnswerOptions
                      optionCount={currentQuestion.option_count}
                      selectedOption={currentState?.selectedOption ?? null}
                      isSavingAnswer={isSavingAnswer}
                      isQuizActive={isQuizActive}
                      onOptionSelect={handleOptionSelect}
                    />

                    {/* Navigation Buttons */}
                    <QuestionNavigationButtons
                      currentQuestionIndex={currentQuestionIndex}
                      questionsLength={questions.length}
                      isQuizActive={isQuizActive}
                      isSavingAnswer={isSavingAnswer}
                      onPrevious={handlePrevious}
                      onNext={handleNext}
                      onSkip={handleSkip}
                      onSubmit={showSubmissionConfirmation}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Question Navigator Sidebar - Hidden on mobile */}
            <div className="col-lg-4 d-none d-lg-block">
              <QuestionNavigatorSidebar
                filteredQuestions={getFilteredQuestions()}
                questions={questions}
                questionsLength={questions.length}
                questionStates={questionStates}
                currentQuestionIndex={currentQuestionIndex}
                isQuizActive={isQuizActive}
                isSavingAnswer={isSavingAnswer}
                activeFilters={activeFilters}
                extensionsUsed={extensionsUsed}
                toggleFilter={toggleFilter}
                setCurrentQuestionIndex={setCurrentQuestionIndex}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigator Drawer */}
      {showNavigatorDrawer && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-lg-none" 
          style={{ zIndex: 1050, backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowNavigatorDrawer(false)}
        >
          <div 
            className="position-absolute top-0 end-0 h-100 bg-white shadow-lg" 
            style={{ width: '320px', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <h6 className="mb-0 fw-bold">Question Navigator</h6>
              <button 
                className="btn btn-danger rounded-circle shadow"
                onClick={() => setShowNavigatorDrawer(false)}
                style={{ width: '40px', height: '40px' }}
                title="Close Navigator"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="p-3">
              <QuestionNavigatorSidebar
                filteredQuestions={getFilteredQuestions()}
                questions={questions}
                questionsLength={questions.length}
                questionStates={questionStates}
                currentQuestionIndex={currentQuestionIndex}
                isQuizActive={isQuizActive}
                isSavingAnswer={isSavingAnswer}
                activeFilters={activeFilters}
                extensionsUsed={extensionsUsed}
                toggleFilter={toggleFilter}
                setCurrentQuestionIndex={(index) => {
                  setCurrentQuestionIndex(index);
                  setShowNavigatorDrawer(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Image Modal with Pinch-to-Zoom */}
      {showImageModal && currentQuestion.question_img_url && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100" 
          style={{ zIndex: 1055, backgroundColor: 'rgba(0,0,0,0.9)' }}
          onClick={() => {
            setShowImageModal(false);
            setImageScale(1);
            setImagePosition({ x: 0, y: 0 });
          }}
        >
          <div className="d-flex justify-content-center align-items-center h-100 p-3">
            <div className="position-relative">
              <img 
                src={currentQuestion.question_img_url} 
                alt="Question - Full Size" 
                className="img-fluid"
                style={{ 
                  maxHeight: '90vh', 
                  maxWidth: '90vw',
                  transform: `scale(${imageScale}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                  transformOrigin: 'center',
                  transition: isDragging ? 'none' : 'transform 0.2s ease',
                  cursor: imageScale > 1 ? 'move' : 'zoom-in',
                  touchAction: 'none',
                  userSelect: 'none'
                }}
                onWheel={(e) => {
                  e.preventDefault();
                  const delta = e.deltaY > 0 ? -0.1 : 0.1;
                  setImageScale(prev => Math.min(Math.max(prev + delta, 0.5), 3));
                }}
                onMouseDown={(e) => {
                  if (imageScale > 1) {
                    e.preventDefault();
                    setIsDragging(true);
                    setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
                  }
                }}
                onMouseMove={(e) => {
                  if (isDragging && imageScale > 1) {
                    e.preventDefault();
                    setImagePosition({
                      x: e.clientX - dragStart.x,
                      y: e.clientY - dragStart.y
                    });
                  }
                }}
                onMouseUp={() => {
                  setIsDragging(false);
                }}
                onMouseLeave={() => {
                  setIsDragging(false);
                }}
                onTouchStart={(e) => {
                  if (e.touches.length === 1 && imageScale > 1) {
                    e.preventDefault();
                    const touch = e.touches[0];
                    setIsDragging(true);
                    setDragStart({ x: touch.clientX - imagePosition.x, y: touch.clientY - imagePosition.y });
                  } else if (e.touches.length === 2) {
                    e.preventDefault();
                  }
                }}
                onTouchMove={(e) => {
                  if (e.touches.length === 1 && isDragging && imageScale > 1) {
                    e.preventDefault();
                    const touch = e.touches[0];
                    setImagePosition({
                      x: touch.clientX - dragStart.x,
                      y: touch.clientY - dragStart.y
                    });
                  } else if (e.touches.length === 2) {
                    e.preventDefault();
                    // Basic pinch-to-zoom (simplified)
                    const touch1 = e.touches[0];
                    const touch2 = e.touches[1];
                    const distance = Math.sqrt(
                      Math.pow(touch2.clientX - touch1.clientX, 2) + 
                      Math.pow(touch2.clientY - touch1.clientY, 2)
                    );
                    // This is a simplified version - a more complex implementation would track initial distance
                    if (distance > 100) {
                      setImageScale(prev => Math.min(prev + 0.1, 3));
                    } else if (distance < 50) {
                      setImageScale(prev => Math.max(prev - 0.1, 0.5));
                    }
                  }
                }}
                onTouchEnd={() => {
                  setIsDragging(false);
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          
          {/* Close Button - Top Right, Outside Image Area */}
          <div className="position-fixed top-0 end-0 m-3" style={{ zIndex: 1056 }}>
            <button 
              className="btn btn-danger rounded-circle shadow-lg"
              onClick={(e) => {
                e.stopPropagation();
                setShowImageModal(false);
                setImageScale(1);
                setImagePosition({ x: 0, y: 0 });
                setIsDragging(false);
              }}
              style={{ width: '48px', height: '48px' }}
              title="Close Image"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          {/* Zoom Controls - Sticky Bottom */}
          <div className="position-fixed bottom-0 start-50 translate-middle-x mb-4" style={{ zIndex: 1056 }}>
            <div className="d-flex gap-2 justify-content-center">
              <button 
                className="btn btn-light btn-sm rounded-circle shadow"
                onClick={(e) => {
                  e.stopPropagation();
                  setImageScale(prev => Math.min(prev + 0.5, 3));
                }}
                style={{ width: '44px', height: '44px' }}
                title="Zoom In"
              >
                <i className="fas fa-plus"></i>
              </button>
              <button 
                className="btn btn-light btn-sm rounded-circle shadow"
                onClick={(e) => {
                  e.stopPropagation();
                  setImageScale(prev => Math.max(prev - 0.5, 0.5));
                }}
                style={{ width: '44px', height: '44px' }}
                title="Zoom Out"
              >
                <i className="fas fa-minus"></i>
              </button>
              <button 
                className="btn btn-light btn-sm rounded-circle shadow"
                onClick={(e) => {
                  e.stopPropagation();
                  setImageScale(1);
                  setImagePosition({ x: 0, y: 0 });
                }}
                style={{ width: '44px', height: '44px' }}
                title="Reset Zoom"
              >
                <i className="fas fa-expand-arrows-alt"></i>
              </button>
            </div>
            {/* Zoom Level Indicator */}
            <div className="text-center mt-2">
              <span className="badge bg-dark bg-opacity-75 text-white px-3 py-1">
                {Math.round(imageScale * 100)}%
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Navigator Button - Mobile Only */}
      <div className="d-lg-none position-fixed bottom-0 end-0" style={{ zIndex: 1045, margin:'20px',marginBottom: '110px' }}>
        <button 
          className="btn btn-primary rounded-circle shadow-lg"
          style={{ width: '56px', height: '56px' }}
          onClick={() => setShowNavigatorDrawer(true)}
        >
          <i className="fas fa-th-large"></i>
        </button>
      </div>

      {/* Quiz Submission Confirmation Dialog Modal */}
      {showSubmissionDialog && (
        <ConfirmationForm
          message={"Are you sure you want to submit the quiz?"}
          onConfirm={handleSubmissionConfirm}
          onCancel={() => setShowSubmissionDialog(false)}
          confirmLabel="Submit Quiz"
          cancelLabel="Continue Quiz"
          stats={getQuizStats()}
        />
      )}

      {/* Time Extension Dialog Modal */}
      {showExtensionDialog && (
        <TimeExtensionPopup
          isOpen={showExtensionDialog}
          onExtend={() => handleTimeExtension(true)}
          onClose={() => handleTimeExtension(false)}
          extensionMinutes={2}
          message={"You have less than 1 minute remaining! Would you like to extend your quiz time by 2 minutes?"}
        />
      )}

      {/* Flag Question Dialog Modal */}
      <FlagQuestionDialog
        isOpen={showFlagQuestionDialog}
        questionNumber={currentQuestionIndex + 1}
        onConfirm={handleFlagQuestion}
        onCancel={() => setShowFlagQuestionDialog(false)}
        isLoading={isFlaggingQuestion}
      />

      {/* Flag Success Notification Toast */}
      {showFlagSuccessNotification && (
        <div className="position-fixed top-0 end-0 m-3" style={{ zIndex: 1055 }}>
          <div className="alert alert-warning alert-dismissible fade show shadow" role="alert">
            <i className="fas fa-flag me-2"></i>
            <strong>Question Reported!</strong> Thank you for reporting this question. It will be reviewed by administrators.
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setShowFlagSuccessNotification(false)}
            ></button>
          </div>
        </div>
      )}

      {/* Success Notification Toast */}
      {showSuccessNotification && (
        <div className="position-fixed top-0 end-0 m-3" style={{ zIndex: 1055 }}>
          <div className="alert alert-success alert-dismissible fade show shadow" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            <strong>Time Extended!</strong> 2 minutes added to your quiz time.
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setShowSuccessNotification(false)}
            ></button>
          </div>
        </div>
      )}

      {/* State Restored Notification Toast */}
      {showStateRestoredNotification && (
        <div className="position-fixed top-0 end-0 m-3" style={{ zIndex: 1054 }}>
          <div className="alert alert-info alert-dismissible fade show shadow" role="alert">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Progress Restored!</strong> Your quiz progress has been restored from where you left off.
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setShowStateRestoredNotification(false)}
            ></button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizPage;
